import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import axios from 'src/lib/axios';
import { type MutationConfig } from 'src/lib/react-query';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';

import type { User } from 'src/features/auth';
import type { GeneralSmartParkAllErrors } from 'src/types';

const RESOURCE = '/users/:userId/';

// Not all fields are required since we have multiple forms for each user update
const formSchema = z
    .object({
        first_name: z.string().min(3, { message: validationMessagesMap.validationErrorFirstName }).max(24).optional(),
        last_name: z.string().min(3, { message: validationMessagesMap.validationErrorLastName }).max(24).optional(),
        email: z
            .string({
                required_error: validationMessagesMap.validationErrorEmail,
            })
            .min(1, { message: validationMessagesMap.validationErrorEmail })
            .email({ message: validationMessagesMap.validationErrorEmail })
            .optional(),
        lang: z.string().optional(),
        old_password: z.string().min(3).optional(),
        new_password: z.string().optional(),
        confirm_new_password: z.string().optional(),
    }) // Confirm passwords match
    .superRefine(({ new_password, confirm_new_password }, ctx) => {
        if (new_password || confirm_new_password) {
            if (new_password !== confirm_new_password) {
                ctx.addIssue({
                    path: ['confirm_new_password'],
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorConfirmPassword,
                });
            }
        }
    });
type FormSchemaValues = z.infer<typeof formSchema>;

interface DTO extends Omit<FormSchemaValues, 'confirm_new_password'> {
    userId: string;
}

type MutationFnType = typeof mutationFn;
const mutationFn = ({ userId, ...data }: DTO): Promise<User> => {
    return axios.patch(formatResourcePath(RESOURCE, { userId }), data);
};

// We can handle all user updates from this one hook
const useMutationHook = (options: MutationConfig<MutationFnType, GeneralSmartParkAllErrors> = {}) =>
    useMutation({
        mutationFn,
        ...options,
    });

export { RESOURCE as USER_UPDATE_RESOURCE, formSchema as updateUserFormSchema, useMutationHook as useUpdateUser };

export type { DTO as UpdateUserDTO, FormSchemaValues as UpdateUserFormSchemaValues };
